/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, FC, useRef, MutableRefObject } from 'react';
import Landing from '../../../Models/LandingModels/Landing';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import User from '../../../Models/UserModels/User';
import {
  Box,
  Paper,
  Stack,
} from '@mui/material';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { LandingOverview } from '../../../Models/ReportModels/LandingOverview';
import { getLandingById } from '../../../Services/LondunarkerfiAPIService';
import { FilterPayload } from './Filters/FilterDefinition';

interface LandingReportsProps {
  user: User;
  apiRef: MutableRefObject<GridApiCommunity>;
  toggleDashboardOpen: (open: boolean) => void;
  refetchLandings: () => Promise<void>;
  onLandingSelect: (landing: Landing, newLandingId?: number, landings?: Landing[], newWeightNoteId?: number) => void;
  dateFrom: string;
  setDateFrom: (date: string) => void;
  dateTo: string;
  setDateTo: (date: string) => void;
  loading: boolean;
  landings: LandingOverview[];
  hasMore: boolean;
  page: number;
  setPage: (page: number | ((prev: number) => number)) => void;
  fetchLandings: (pageToFetch: number, reset: boolean, filterModel?: FilterPayload) => void;
}


const LandingReport: FC<LandingReportsProps> = (props) => {
  const gridRef = useRef<HTMLDivElement>(null);

  const handleLandingSelection = async (landingId: number) => {
    const landing = await getLandingById(landingId);
    await props.refetchLandings();
    props.onLandingSelect(landing, landing.id);
    props.toggleDashboardOpen(false);
  };

  // Custom infinite scroll handler
  useEffect(() => {
    const gridNode = gridRef.current;
    if (!gridNode) return;

    const handleScroll = () => {
      if (props.loading || !props.hasMore) return;
      const gridWindow = gridNode.querySelector('.MuiDataGrid-virtualScroller');
      if (!gridWindow) return;
      const { scrollTop, scrollHeight, clientHeight } = gridWindow as HTMLElement;
      if (scrollHeight - scrollTop - clientHeight < 100) {
        // Near bottom, fetch next page
        props.setPage((prev: number) => prev + 1);
      }
    };

    const gridWindow = gridNode.querySelector('.MuiDataGrid-virtualScroller');
    if (gridWindow) {
      gridWindow.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (gridWindow) {
        gridWindow.removeEventListener('scroll', handleScroll);
      }
    };
  }, [props]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', minWidth: 60, filterable: false, sortable: false },
    {
      field: 'landingNumber',
      headerName: 'Löndun #',
      flex: 0.7,
      minWidth: 100,
      sortable: true,
    },
    {
      field: 'shipRegistrationNumber',
      headerName: 'Skip númer',
      flex: 1,
      minWidth: 100,
      valueGetter: (params) => params.row.ship?.shipRegistrationNumber,
      sortable: true,
    },
    {
      field: 'shipName',
      headerName: 'Skip heiti',
      flex: 1.2,
      minWidth: 160,
      valueGetter: (params) => params.row.ship?.name,
      sortable: true,
    },
    {
      field: 'landingStarts',
      headerName: 'Löndun hefst',
      flex: 1,
      minWidth: 180,
      type: 'date',
      valueGetter: (params) => params.row.landingStarts ? new Date(params.row.landingStarts) : null,
      renderCell: (params) => {
        const date = params.row.landingStarts
          ? dayjs(params.row.landingStarts).format('DD.MM.YYYY')
          : '';
        return <>{date}</>;
      },
      sortable: true,
      filterable: false,
    },
    {
      field: 'landingClosedAt',
      headerName: 'Löndun lokið',
      flex: 1,
      minWidth: 180,
      type: 'date',
      valueGetter: (params) => params.row.landingClosedAt ? new Date(params.row.landingClosedAt) : null,
      renderCell: (params) => {
        const date = params.row.landingClosedAt
          ? dayjs(params.row.landingClosedAt).format('DD.MM.YYYY HH:mm')
          : '';
        return <>{date}</>;
      },
      sortable: true,
      filterable: false,
    },
    {
      field: 'buyerName',
      headerName: 'Kaupandi nafn',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.buyer?.name,
      sortable: true,
    },
    {
      field: 'buyerSSN',
      headerName: 'Kaupandi kennitala',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.buyer?.ssn,
      sortable: true,
    },
    {
      field: 'stock',
      headerName: 'Fisktegund',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.stock?.name,
      sortable: true,
    },
    {
      field: 'amount',
      headerName: 'Magn (kg)',
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => params.value ? params.value.toLocaleString('is-IS', { maximumFractionDigits: 2 }) : '',
      sortable: true,
      type: 'number',
    },
    {
      field: 'deductionType',
      headerName: 'Umbúðir',
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'deductionWeight',
      headerName: 'Eining',
      flex: 1,
      minWidth: 60,
      valueFormatter: (params) => params.value ? params.value.toLocaleString('is-IS', { maximumFractionDigits: 2 }) : '',
      sortable: true,
      type: 'number',
    },
    {
      field: 'deductionQuantity',
      headerName: 'fjöldi',
      flex: 1,
      minWidth: 60,
      valueFormatter: (params) => params.value ? params.value.toLocaleString('is-IS', { maximumFractionDigits: 0 }) : '',
      sortable: true,
      type: 'number',
    },
  ];

  const formatDate = (date: dayjs.Dayjs) => date.format('DD.MM.YYYY');

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="is">
      <Paper elevation={3} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <DatePicker
              label="Frá"
              value={dayjs(props.dateFrom, 'DD.MM.YYYY')}
              onChange={date => {
                if (date) props.setDateFrom(formatDate(date));
              }}
              disableFuture
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 150 }
                }
              }}
            />
            <DatePicker
              label="Til"
              value={dayjs(props.dateTo, 'DD.MM.YYYY')}
              onChange={date => {
                if (date) props.setDateTo(formatDate(date));
              }}
              disableFuture
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 150 }
                }
              }}
            />
          </Stack>
        </Box >
        <Box sx={{ flexGrow: 1, height: '100%', minHeight: '400px' }}>
          <div ref={gridRef} style={{ height: '100%' }}>
            <DataGrid
              apiRef={props.apiRef}
              rows={props.landings}
              columns={columns}
              disableColumnFilter
              loading={props.loading}
              columnVisibilityModel={{id: false}}
              density="standard"
              getRowHeight={() => 'auto'}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'landingStarts', sort: 'desc' }],
                },
              }}
              onRowClick={(params) => {
                handleLandingSelection(params.row?.landingNumber);
              }}
              sx={{
                '& .MuiDataGrid-row:hover': {
                  cursor: 'pointer',
                },
                '& .MuiDataGrid-paper': {
                  minWidth: 380,
                  maxWidth: 420,
                },
                '& .MuiDataGrid-filterForm': {
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  width: '100%',
                  gap: 2,
                },
              }}
              localeText={{
                toolbarFilters: "Síur",
                toolbarFiltersTooltipHide: "Fela síur",
                toolbarFiltersTooltipShow: "Sýna síur",
                columnHeaderFiltersLabel: "Sía eftir",
                filterOperatorContains: "Inniheldur",
                filterOperatorEquals: "Jafnt og",
                filterOperatorStartsWith: "Byrjar á",
                filterOperatorEndsWith: "Endar á",
                filterOperatorIs: "Er",
                filterOperatorNot: "Er ekki",
                filterOperatorAfter: "Eftir",
                filterOperatorOnOrAfter: "Á eða eftir",
                filterOperatorBefore: "Fyrir",
                filterOperatorOnOrBefore: "Á eða fyrir",
                filterOperatorIsEmpty: "Er tómt",
                filterOperatorIsNotEmpty: "Er ekki tómt",
                filterValueAny: "(einhver)",
                filterValueTrue: "satt",
                filterValueFalse: "ósatt",
                filterOperatorIsAnyOf: "Er einhver af",
                toolbarColumns: "Dálkar",
                toolbarColumnsLabel: "Dálkar",
                columnsPanelTextFieldLabel: "Finna dálk",
                columnsPanelTextFieldPlaceholder: "Leita að dálki",
                columnsPanelDragIconLabel: "Endurraða dálkum",
                columnsPanelShowAllButton: "Sýna alla",
                columnsPanelHideAllButton: "Fela alla",
                filterPanelColumns: "Dálkar",
                filterPanelOperator: "Skilyrði",
                filterPanelInputLabel: "Gildi",
                filterPanelInputPlaceholder: "Sía gildi",
                filterPanelAddFilter: "Bæta við síu",
                filterPanelDeleteIconLabel: "Eyða",
                columnMenuLabel: "Valmynd",
                columnMenuShowColumns: "Sýna dálka",
                columnMenuFilter: "Sía",
                columnMenuHideColumn: "Fela",
                columnMenuUnsort: "Hætta að raða",
                columnMenuSortAsc: "Raða hækkandi",
                columnMenuSortDesc: "Raða lækkandi",
                columnMenuManageColumns: "Stjórna dálkum",
              }}
            />
          </div>
        </Box>
      </Paper>
    </LocalizationProvider>
  );
};

export default LandingReport;
